import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { faBolt, faClipboard, faPlus, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { faBolt as faBoltRegular, faCirclePlus, faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import ActivityList from './activity_list';
import ActivityModal from 'bundles/apex/activities/modal';
import LogActivityModal from 'bundles/apex/activities/log_activity/modal';
import SendEmailModal from 'bundles/apex/activities/send_email/modal';
import ReauthModal from 'bundles/apex/activities/send_email/reauth_modal';
import {
  clearPendingReply, fromCompose, shouldAutoOpenCompose, takePendingReply
} from 'bundles/apex/activities/send_email/helpers';
import { Button } from 'components/ui/button';
import CopyOverlay from 'components/copy_overlay';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { Separator } from '@buildoutinc/blueprint-react/ui/Separator';
import Filters from 'bundles/apex/activities/filters';
import LoadingSpinner from 'components/ui/loading_spinner';
import { buildActivity, pinActivity } from 'bundles/apex/activities/form/helpers';
import { groupByPeriod as groupActivitiesByPeriod, mergeStageEvents } from 'bundles/apex/activities/timeline_helpers';
import { useActivitiesState } from 'bundles/apex/activities/hooks';
import * as filterHelpers from './filters/helpers';
import Pagination from 'components/ui/pagination';
import classNames from 'classnames';
import { stripQueryParameter } from 'helpers/browser_helpers';

import type { Activity, Features, FilterOptions, PaginationData, TaskConfig } from 'bundles/apex/activities/types';
import type { PendingReply, SendEmailConfig } from 'bundles/apex/activities/send_email/types';
import type { ActivityFilters, FilterBarProps } from './filters/types';
import type { MenuOptions } from 'bundles/apex/activities/types';
import type { Parent, ParentOption } from 'bundles/crm/types';
import type { Paths } from 'bundles/apex/activities/hooks';
import { showSnackbar } from 'components/ui/snackbar/helper';

export interface ActivitiesProps {
  activities: Activity[];
  activityMenuOptions?: MenuOptions;
  aiEnabled?: boolean;
  parentId?: number;
  parentType?: string;
  importerProps: any;
  isBackofficeAdmin?: boolean;
  forUserId?: number;
  currentParent?: Parent | Parent[];
  features?: Features;
  options?: {
    parents?: ParentOption[];
  }
  filters?: boolean | ActivityFilters;
  filterOptions?: FilterOptions;
  // Split the timeline under Today / This week / Earlier this month / Earlier this year / year headers
  groupByPeriod?: boolean;
  header?: boolean;
  hideAfterReparenting?: boolean;
  // The page has its own Log Activity composer, which reopens after a reconnect instead of the modal.
  pageComposer?: boolean;
  showRedesignModal?: boolean;
  subjectName?: string;
  // Render the header actions (send email, add activity) without the timeline itself.
  // For viewers who may contribute to a record but not read its history.
  hideList?: boolean;
  inboxAddress?: string;
  listClassName?: string;
  paginate?: boolean;
  paths?: { availableTags?: string, create?: string, latest?: string } & Paths,
  renderFilters?: (props: FilterBarProps) => ReactNode;
  seedFiltersFromUrl?: boolean;
  sendEmail?: SendEmailConfig;
  showParents?: boolean | 'path';
  showPinned?: boolean;
  showNeedsReply?: boolean;
  smallCreateButton?: boolean;
  tagText?: string;
  taskConfig?: TaskConfig;
  // Render rows in the contact timeline's lighter layout (see ActivityItem)
  timeline?: boolean;
  withNeedsReplyCount?: boolean;
  // Interleave the stage changes of the deals a Buildout OS contact is represented on
  withStageEvents?: boolean;
  onChange?: (activities: Activity[]) => void;
  onEmptyStateClick?: () => void;
  onPageDataChange?: (pageData: PaginationData) => void;
}

export default function Activities({
  activities: activitiesProp,
  activityMenuOptions,
  aiEnabled,
  currentParent,
  parentId,
  parentType,
  features,
  options,
  filters: showFilters,
  filterOptions,
  groupByPeriod,
  header,
  hideAfterReparenting,
  hideList,
  inboxAddress,
  listClassName,
  showRedesignModal,
  pageComposer,
  paginate,
  paths,
  renderFilters,
  seedFiltersFromUrl,
  sendEmail,
  showParents,
  showPinned,
  showNeedsReply,
  smallCreateButton,
  subjectName,
  forUserId,
  isBackofficeAdmin,
  tagText,
  taskConfig,
  timeline,
  withNeedsReplyCount,
  withStageEvents,
  onChange,
  onEmptyStateClick,
  onPageDataChange,
}: ActivitiesProps) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [resumeReply, setResumeReply] = useState<PendingReply | null>(null);

  // Returning from the reauth OAuth flow lands here with ?compose_email=1; reopen the compose
  // modal for the contact the broker started from, then strip the flag so a refresh is a no-op.
  useEffect(() => {
    const pending = takePendingReply(window.location.search, sendEmail);

    if (pending) {
      setResumeReply(pending);
    } else if (!pageComposer && shouldAutoOpenCompose(window.location.search, sendEmail)) {
      setShowSendEmailModal(true);
    }

    if (fromCompose(window.location.search)) stripQueryParameter('compose_email');
  }, []);
  const {
    activities,
    pinnedActivities,
    stageEvents,
    stageOptions,
    loading,
    error,
    reload,
    needsReplyCount,
    pageData,
    addActivity,
    removeActivity,
    replaceActivity,
    changePinned,
    filters,
    changeFilters,
  } = useActivitiesState({
    activities: activitiesProp,
    paginate,
    paths,
    seedFiltersFromUrl,
    showPinned,
    withNeedsReplyCount,
    withStageEvents,
    onChange,
    onPageDataChange,
  });
  const autoExpandEmails = filterHelpers.hasContentFilters(filters);
  const timelineItems = mergeStageEvents(activities, stageEvents);

  const handleTogglePin = async (activity: Activity) => {
    const pinned = !activity.pinned;

    try {
      const saved: Activity = await pinActivity(activity, pinned);
      changePinned(saved, pinned);
    } catch {
      showSnackbar({ text: 'Something went wrong. ', variant: 'danger' });
    }
  };

  const activityListProps = {
    activityMenuOptions,
    aiEnabled,
    currentParent,
    autoExpandEmails,
    forUserId,
    hideAfterReparenting,
    isBackofficeAdmin,
    options,
    showParents,
    showPinned,
    showRedesignModal,
    showNeedsReply,
    stageOptions,
    subjectName,
    tagText,
    sendEmail,
    taskConfig,
    timeline,
    resumeReply,
    onDelete: removeActivity,
    onResumeConsumed: () => setResumeReply(null),
    onSave: replaceActivity,
    onTogglePin: handleTogglePin,
  };

  const sectionDivider = (label: string) => {
    if (timeline) {
      return <div className="activity-group-header">{label}</div>;
    }

    return (
      <div className="d-flex align-items-center gap-2 mb-2">
        <Separator className="flex-grow-1" />
        <span className="text-nowrap small text-uppercase text-muted fw-semibold">{label}</span>
        <Separator className="flex-grow-1" />
      </div>
    );
  };

  const showEmpty = timelineItems.length === 0 && pinnedActivities.length === 0 && !loading && !error &&
    !showFilters && !renderFilters && !hideList;

  let onClick;
  if (onEmptyStateClick) {
    onClick = onEmptyStateClick;
  } else if (paths?.create) {
    onClick = () => setShowCreateModal(true);
  }

  return (
    <>
      {showEmpty ? (
        <Empty className="bg-white">
          <Empty.Media>
            <FontAwesomeIcon icon={faBolt} />
          </Empty.Media>
          <Empty.Title>No Activities to Display</Empty.Title>
          <Empty.Content>Get started by adding an activity</Empty.Content>
          {onClick && (
            <Empty.Actions>
              <Button icon={faPlus} label="Add activity" onClick={onClick} />
            </Empty.Actions>
          )}
        </Empty>
      ) : (
        <div className="d-flex flex-column flex-grow-1 overflow-hidden gap-3">
          {header && (
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon className="me-2 text-primary" icon={faBoltRegular} size="lg" />
                <h6 className="fw-bold" key="header">Activities</h6>
                {inboxAddress && (
                  <CopyOverlay
                    className="text-muted"
                    dark
                    text={inboxAddress}
                    tooltip="Copy inbox address for this record"
                  >
                    <FontAwesomeIcon icon={faClipboard} />
                  </CopyOverlay>
                )}
              </div>
              <div className="d-flex align-items-center gap-2">
                {sendEmail?.enabled && sendEmail.path && (
                  <Button
                    icon={faEnvelope}
                    key="send-email"
                    variant="outline"
                    onClick={() => (sendEmail.canSend ? setShowSendEmailModal(true) : setShowReauthModal(true))}
                  >
                    Send Email
                  </Button>
                )}
                {paths?.create && (
                  smallCreateButton ? (
                    <Button
                      icon={faCirclePlus}
                      key="add"
                      label="Add Activity"
                      size="sm"
                      variant="outline"
                      onClick={() => setShowCreateModal(true)}
                    />
                  ) : (
                    <Button icon={faPlus} key="add" onClick={() => setShowCreateModal(true)}>
                      New activity
                    </Button>
                  ))}
              </div>
            </div>
          )}

          {renderFilters ? renderFilters({ filters, needsReplyCount, onChange: changeFilters }) : (
            <Filters
              features={features}
              filterOptions={filterOptions}
              filters={filters}
              show={showFilters}
              onChange={changeFilters}
            />
          )}

          {!hideList && (error ? (
            <Empty>
              <Empty.Media>
                <FontAwesomeIcon icon={faTriangleExclamation} />
              </Empty.Media>
              <Empty.Title>Couldn't load activities</Empty.Title>
              <Empty.Content>Something went wrong while loading activities.</Empty.Content>
              <Empty.Actions>
                <Button label="Try again" onClick={reload} />
              </Empty.Actions>
            </Empty>
          ) : loading ? (
            <LoadingSpinner />
          ) : (
            <div className={classNames(listClassName)}>
              {showPinned && pinnedActivities.length > 0 && (
                <div className="d-flex flex-column mb-3">
                  {sectionDivider('Pinned')}
                  <ActivityList activities={pinnedActivities} {...activityListProps} />
                </div>
              )}
              {!(showPinned && timelineItems.length === 0 && pinnedActivities.length > 0) && (
                groupByPeriod && timelineItems.length > 0 ? (
                  groupActivitiesByPeriod(timelineItems).map(group => (
                    <div className="d-flex flex-column mb-3" key={group.label}>
                      {sectionDivider(group.label)}
                      <ActivityList activities={group.activities} {...activityListProps} />
                    </div>
                  ))
                ) : (
                  <ActivityList activities={timelineItems} {...activityListProps} />
                )
              )}
            </div>
          ))}

          {(!hideList && !loading && !error && pageData) &&
            <Pagination
              currentPage={pageData.currentPage}
              totalPages={pageData.totalPages}
              onChange={page => changeFilters({ page })}
            />
          }
        </div>
      )}

      {showRedesignModal && paths?.create ? (
        <LogActivityModal
          aiEnabled={aiEnabled}
          fromEmail={sendEmail?.fromEmail}
          parentId={parentId}
          parentType={parentType}
          path={paths.create}
          sendEmail={sendEmail}
          show={showCreateModal}
          subjectName={subjectName}
          onHide={() => setShowCreateModal(false)}
          onLogged={(activity) => {
            addActivity(activity);
            setShowCreateModal(false);
          }}
        />
      ) : (
        <ActivityModal
          activity={buildActivity(paths?.create, { availableTagsPath: paths?.availableTags })}
          header="Add activity"
          options={options}
          show={showCreateModal}
          tagText={tagText}
          onHide={() => setShowCreateModal(false)}
          onSave={(activity) => {
            addActivity(activity);
            setShowCreateModal(false);
          }}
        />
      )}

      {showSendEmailModal && sendEmail?.path && (
        <SendEmailModal
          path={sendEmail.path}
          to={sendEmail.to}
          onClose={() => setShowSendEmailModal(false)}
        />
      )}

      {showReauthModal && sendEmail?.contactId && (
        <ReauthModal
          authPath={sendEmail.authPath}
          connected={sendEmail.connected}
          contactId={sendEmail.contactId}
          expired={sendEmail.expired}
          providerName={sendEmail.providerName}
          onClose={() => {
            clearPendingReply();
            setShowReauthModal(false);
          }}
        />
      )}
    </>
  );
}
